import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Accent } from './primitives';

/* The scene only promises a reveal, since the next page sells the community: the lines build
   anticipation for what is behind the door, and the middle one slips the social proof back in. */
const steps = ['Cracking the vault…', '150+ students already inside…', 'Opening the door…'];
const COINS = 22;
/* Used when the pressed button has no solid face colour to grow from. */
const FALLBACK_FACE = '#00d632';

/* The pressed button grows into a loading scene, the next page is swapped in underneath while
   the scene covers everything, then the scene lifts away like a curtain. */
export function JoinWarp({
  from,
  onReveal,
  onDone,
}: {
  from: HTMLElement;
  onReveal: () => void;
  onDone: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const done = useRef(onDone);
  done.current = onDone;
  const reveal = useRef(onReveal);
  reveal.current = onReveal;
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const sheet = element.querySelector<HTMLElement>('.warp-sheet')!;
    const status = element.querySelector<HTMLElement>('.warp-status')!;
    // Everything is measured against the scene's own box, not innerWidth/innerHeight, so a
    // desktop scrollbar or a mobile toolbar can't shift the start shape off the button.
    const box = element.getBoundingClientRect();
    const width = box.width;
    const height = box.height;
    const rect = from.getBoundingClientRect();
    const style = getComputedStyle(from);
    const radius = Math.min(parseFloat(style.borderTopLeftRadius) || 0, rect.height / 2);
    const face =
      style.backgroundColor && style.backgroundColor !== 'rgba(0, 0, 0, 0)'
        ? style.backgroundColor
        : FALLBACK_FACE;
    // The clip is tweened as plain numbers and written out each frame. Handing GSAP the
    // inset() strings made it read the start back from the browser, which shortens
    // "inset(a b c b round r)" to three sides: the numbers paired up wrong, the radius became
    // the left edge, and the shape jumped sideways and lost its corners as it began to grow.
    const clip = {
      top: rect.top - box.top,
      right: box.right - rect.right,
      bottom: box.bottom - rect.bottom,
      left: rect.left - box.left,
      round: radius,
    };
    const drawClip = () => {
      sheet.style.clipPath = `inset(${clip.top}px ${clip.right}px ${clip.bottom}px ${clip.left}px round ${clip.round}px)`;
    };
    drawClip();
    // Same for the curtain's bowed lower edge on the way out.
    const bow = { depth: 0 };
    const drawBow = () => {
      sheet.style.borderRadius = `0 0 50% 50% / 0 0 ${bow.depth}vh ${bow.depth}vh`;
    };
    const originX = rect.left - box.left + rect.width / 2;
    const originY = rect.top - box.top + rect.height / 2;
    element.style.setProperty('--warp-face', face);

    // A pixel-exact copy of the pressed button (label, icon, colour, shape) sits where the
    // button is, so the first frame is identical to what was clicked.
    const clone = from.cloneNode(true) as HTMLElement;
    clone.removeAttribute('id');
    clone.setAttribute('aria-hidden', 'true');
    clone.tabIndex = -1;
    clone.classList.add('warp-clone');
    Object.assign(clone.style, {
      left: `${rect.left - box.left}px`,
      top: `${rect.top - box.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      background: face,
    });
    sheet.insertBefore(clone, sheet.querySelector('.warp-stage'));

    const random = gsap.utils.random;
    const context = gsap.context(() => {
      const timeline = gsap.timeline({ onComplete: () => done.current() });

      // The button bursts open: fastest on the very first frame, easing into the screen edges.
      timeline
        .to(
          clip,
          {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            round: 0,
            duration: 0.7,
            ease: 'power3.out',
            onUpdate: drawClip,
          },
          0,
        )
        // Full screen now: drop the clip so the rest of the scene paints without it.
        .call(
          () => {
            sheet.style.clipPath = 'none';
          },
          [],
          0.7,
        )
        .to(clone, { opacity: 0, scale: 1.2, duration: 0.25, ease: 'power2.out' }, 0)
        .fromTo('.warp-glow', { opacity: 0 }, { opacity: 1, duration: 0.6, ease: 'none' }, 0.3)
        .fromTo(
          '.warp-rays',
          { scale: 0.4, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1.2, ease: 'power3.out' },
          0.35,
        )
        .to('.warp-rays', { rotation: 60, duration: 3.4, ease: 'none' }, 0.35);

      // A shower of coins leaps out of the button and rains past the screen.
      gsap.utils.toArray<HTMLElement>('.warp-coin').forEach((coin, index) => {
        const peak = random(-height * 0.55, -height * 0.2);
        const spin = random(-540, 540);
        const delay = 0.04 + index * 0.012;
        timeline
          .fromTo(
            coin,
            { x: 0, y: 0, scale: 0, rotation: 0 },
            {
              x: random(-width * 0.45, width * 0.45),
              y: peak,
              scale: random(0.7, 1.25),
              rotation: spin / 2,
              duration: 0.55,
              ease: 'power2.out',
            },
            delay,
          )
          .to(
            coin,
            {
              y: height + 80 - originY,
              rotation: spin,
              duration: random(0.7, 1),
              ease: 'power2.in',
            },
            delay + 0.55,
          )
          // A slow flip that never goes edge-on, so they read as coins rather than streaks.
          .to(
            coin,
            { scaleX: 0.4, duration: 0.22, repeat: 5, yoyo: true, ease: 'sine.inOut' },
            delay,
          );
      });

      // He pops in, and the headline teases what the next page reveals.
      timeline
        .fromTo(
          '.warp-face',
          { scale: 0, rotation: -25 },
          { scale: 1, rotation: 0, duration: 0.6, ease: 'back.out(2)' },
          0.4,
        )
        .to(
          '.warp-face',
          { y: -10, duration: 0.45, ease: 'sine.inOut', yoyo: true, repeat: 3 },
          1.1,
        )
        .fromTo(
          '.warp-line > span',
          { yPercent: 110, opacity: 0 },
          { yPercent: 0, opacity: 1, duration: 0.55, stagger: 0.07, ease: 'back.out(1.6)' },
          0.52,
        )
        .fromTo(
          '.warp-loader',
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4, ease: 'power3.out' },
          0.75,
        );

      // The next page mounts now, while the scene hides it: the old page's teardown and the new
      // page's first render, images and payment frames all happen out of sight, not mid-exit.
      timeline.call(() => reveal.current(), [], 1);

      // Progress rushes, pauses, rushes again and snaps full: it reads as real work.
      const fill = '.warp-fill';
      const step = (index: number) => () => {
        status.textContent = steps[index];
      };
      timeline
        .set(fill, { scaleX: 0 }, 0)
        .call(step(0), [], 0.9)
        .to(fill, { scaleX: 0.36, duration: 0.45, ease: 'power2.out' }, 0.95)
        .call(step(1), [], 1.45)
        .to(fill, { scaleX: 0.72, duration: 0.45, ease: 'power2.out' }, 1.5)
        .call(step(2), [], 2)
        .to(fill, { scaleX: 1, duration: 0.3, ease: 'power2.in' }, 2.05);

      // A squash of joy, then the whole scene lifts like a curtain onto the page beneath,
      // its lower edge bowing as it rises; the page settles up into place behind it.
      timeline
        .to('.warp-face', { scale: 1.15, duration: 0.14, ease: 'power2.out' }, 2.35)
        .to('.warp-face', { scale: 1, duration: 0.2, ease: 'power2.inOut' }, 2.49)
        .to(bow, { depth: 12, duration: 0.4, ease: 'power2.in', onUpdate: drawBow }, 2.55)
        .to(element, { yPercent: -112, duration: 0.8, ease: 'power4.inOut' }, 2.55)
        .add(() => {
          const page = document.querySelector('.join-page');
          if (page)
            gsap.fromTo(
              page,
              { y: 48 },
              { y: 0, duration: 0.8, ease: 'power3.out', clearProps: 'transform' },
            );
        }, 2.75);
    }, element);

    return () => {
      context.revert();
      clone.remove();
    };
  }, [from]);

  const center = useRef<{ left: number; top: number } | null>(null);
  if (!center.current) {
    const rect = from.getBoundingClientRect();
    center.current = { left: rect.left + rect.width / 2, top: rect.top + rect.height / 2 };
  }

  return (
    <div className="warp" ref={root} role="status" aria-live="polite">
      <div className="warp-sheet">
        <div className="warp-glow" />
        <div className="warp-rays" />
        <div className="warp-stage">
          <img className="warp-face" src="/images/loader-face.webp" alt="" />
          <h2 className="warp-title">
            <span className="warp-line">
              <span>RRROBLOX</span>
            </span>
            <span className="warp-line">
              <span>
                <Accent>RICH</Accent>
              </span>
            </span>
          </h2>
          <div className="warp-loader">
            <div className="warp-bar">
              <div className="warp-fill" />
            </div>
            <p className="warp-status">{steps[0]}</p>
          </div>
        </div>
      </div>
      <div className="warp-coins" style={center.current}>
        {Array.from({ length: COINS }, (_, index) => (
          <span className="warp-coin" key={index} />
        ))}
      </div>
    </div>
  );
}
